import {
	ApiService,
	type ApiRequestOptions,
	type StandardListParams,
} from '@supply-mover-2-0/core';
import type { ApiResponseDataModel } from '@supply-mover-2-0/shared';
import type { IAutomationApiService } from '../types/apiService';
import type {
	AutomationExecution,
	AutomationFlow,
	AutomationStep,
	AutomationTrigger,
	CreateConnectionRequest,
	CreateFlowRequest,
	CreateStepRequest,
	CreateTriggerRequest,
	FlowTemplate,
	StepConnection,
	StepExecution,
	UpdateFlowRequest,
	UpdateStepRequest,
	UpdateTriggerRequest,
} from '../types/automation';

const API_PREFIX = '/automation';

// Response payload shapes shared by several endpoints below. They mirror the
// inline return types on the methods (kept inline to preserve the public
// signatures verbatim) so the `apiGet` generics don't have to repeat them.
interface ListResponse<TItem> {
	success: boolean;
	data: Array<TItem>;
	pagination: {
		total: number;
		page: number;
		limit: number;
		totalPages: number;
	};
}

interface FlowVersionSummary {
	id: number;
	flowId: number;
	versionNumber: number;
	name?: string;
	description?: string;
	createdAt: string;
	updatedAt: string;
}

interface FlowMetricsResponse {
	batchId: number;
	versionId: number;
	flowName: string;
	totalExecutions: number;
	completedExecutions: number;
	failedExecutions: number;
	pendingExecutions: number;
	steps: Array<{
		stepId: number;
		stepInternalId: string;
		stepName: string;
		stepType: string;
		stepOrder: number;
		parentStepId: number | null;
		totalReached: number;
		completed: number;
		failed: number;
		skipped: number;
		pending: number;
		outcomeBreakdown: Array<{
			key: string;
			label: string;
			count: number;
			percentage: number;
			metadata?: Record<string, unknown>;
		}>;
		outgoingConnections: Array<{
			conditionKey: string;
			toStepId: number;
			toStepInternalId: string;
			executionCount: number;
		}>;
	}>;
}

interface StepExecutionsByOutcomeResponse {
	success: boolean;
	data: Array<{
		id: number;
		executionId: number;
		status: string;
		startedAt: string | null;
		completedAt: string | null;
		durationMs: number | null;
		errorMessage: string | null;
		outcomeKey: string | null;
		entity: {
			type: string | null;
			id: string | null;
			label: string | null;
		};
		execution: {
			id: number;
			status: string;
		};
	}>;
	pagination: {
		total: number;
		page: number;
		pageSize: number;
		totalPages: number;
	};
}

export class AutomationApiService implements IAutomationApiService {
	// ===================
	// FLOW OPERATIONS
	// ===================

	async getFlows(params?: StandardListParams): Promise<{
		success: boolean;
		data: AutomationFlow[];
		pagination: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	}> {
		const response = await ApiService.apiGet<ListResponse<AutomationFlow>>(
			`${API_PREFIX}/flows`,
			{
				page: params?.page,
				limit: params?.pageSize,
				search: params?.search,
				sortFields:
					params?.sort != null ? JSON.stringify(params.sort) : undefined,
				filters:
					typeof params?.filters === 'object'
						? JSON.stringify(params.filters)
						: params?.filters,
				cacheKey: params?.cacheKey,
				cacheTTL: params?.cacheTTL,
			},
		);

		return response;
	}

	async getFlow(
		flowId: number,
		options?: ApiRequestOptions,
	): Promise<AutomationFlow> {
		const response = await ApiService.apiGet<AutomationFlow>(
			`${API_PREFIX}/flows/${flowId}`,
			undefined,
			options,
		);
		return response;
	}

	async getActiveVersionId(flowId: number): Promise<number | null> {
		const response = await ApiService.apiGet<number | null>(
			`${API_PREFIX}/flows/${flowId}/active-version`,
		);
		return response;
	}

	async getVersionInputSchema(
		versionId: number,
	): Promise<{ inputs: Array<any> }> {
		const response = await ApiService.apiGet<{ inputs: Array<unknown> }>(
			`${API_PREFIX}/versions/${versionId}/input-schema`,
		);
		return response;
	}

	async createFlow(data: CreateFlowRequest): Promise<AutomationFlow> {
		const response = await ApiService.apiPost(`${API_PREFIX}/flows`, data);
		return response;
	}

	async updateFlow(
		flowId: number,
		data: UpdateFlowRequest,
	): Promise<AutomationFlow> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/flows/${flowId}`,
			data,
		);
		return response;
	}

	async deleteFlow(flowId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/flows/${flowId}`);
	}

	async copyFlow(flowId: number, name: string): Promise<AutomationFlow> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/copy`,
			{ name },
		);
		return response;
	}

	async publishDraftFlow(flowId: number): Promise<AutomationFlow> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/publish`,
		);
		return response;
	}

	// ===================
	// STEP OPERATIONS
	// ===================

	async getSteps(flowId: number): Promise<AutomationStep[]> {
		const response = await ApiService.apiGet<Array<AutomationStep>>(
			`${API_PREFIX}/flows/${flowId}/steps`,
		);
		return response;
	}

	async getStep(stepId: number): Promise<AutomationStep> {
		const response = await ApiService.apiGet<AutomationStep>(
			`${API_PREFIX}/steps/${stepId}`,
		);
		return response;
	}

	async createStep(data: CreateStepRequest): Promise<AutomationStep> {
		const response = await ApiService.apiPost(`${API_PREFIX}/steps`, data);
		return response;
	}

	async updateStep(
		stepId: number,
		data: UpdateStepRequest,
	): Promise<AutomationStep> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/steps/${stepId}`,
			data,
		);
		return response;
	}

	async deleteStep(stepId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/steps/${stepId}`);
	}

	// ===================
	// CONNECTION OPERATIONS
	// ===================

	async getConnections(flowId: number): Promise<StepConnection[]> {
		const response = await ApiService.apiGet<Array<StepConnection>>(
			`${API_PREFIX}/flows/${flowId}/connections`,
		);
		return response;
	}

	async createConnection(
		data: CreateConnectionRequest,
	): Promise<StepConnection> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/connections`,
			data,
		);
		return response;
	}

	async deleteConnection(connectionId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/connections/${connectionId}`);
	}

	// ===================
	// TRIGGER OPERATIONS
	// ===================

	async getTriggers(params?: StandardListParams): Promise<{
		success: boolean;
		data: AutomationTrigger[];
		pagination: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	}> {
		const response = await ApiService.apiGet<ListResponse<AutomationTrigger>>(
			`${API_PREFIX}/triggers`,
			{
				page: params?.page,
				limit: params?.pageSize,
				search: params?.search,
				sortFields:
					params?.sort != null ? JSON.stringify(params.sort) : undefined,
				filters:
					typeof params?.filters === 'object'
						? JSON.stringify(params.filters)
						: params?.filters,
				cacheKey: params?.cacheKey,
				cacheTTL: params?.cacheTTL,
			},
		);

		return response;
	}

	async getTrigger(triggerId: number): Promise<AutomationTrigger> {
		const response = await ApiService.apiGet<AutomationTrigger>(
			`${API_PREFIX}/triggers/${triggerId}`,
		);
		return response;
	}

	async createTrigger(data: CreateTriggerRequest): Promise<AutomationTrigger> {
		const response = await ApiService.apiPost(`${API_PREFIX}/triggers`, data);
		return response;
	}

	async updateTrigger(
		triggerId: number,
		data: UpdateTriggerRequest,
	): Promise<AutomationTrigger> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/triggers/${triggerId}`,
			data,
		);
		return response;
	}

	async deleteTrigger(triggerId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/triggers/${triggerId}`);
	}

	// ===================
	// EXECUTION OPERATIONS
	// ===================

	async getExecutions(params?: {
		flowId?: number;
		status?: string;
		limit?: number;
		offset?: number;
	}): Promise<AutomationExecution[]> {
		const response = await ApiService.apiGet<Array<AutomationExecution>>(
			`${API_PREFIX}/executions`,
			{ params },
		);
		return response;
	}

	async getExecution(executionId: number): Promise<AutomationExecution> {
		const response = await ApiService.apiGet<AutomationExecution>(
			`${API_PREFIX}/executions/${executionId}`,
		);
		return response;
	}

	async pauseExecution(executionId: number): Promise<AutomationExecution> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/executions/${executionId}/pause`,
		);
		return response;
	}

	async resumeExecution(executionId: number): Promise<AutomationExecution> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/executions/${executionId}/resume`,
		);
		return response;
	}

	async cancelExecution(executionId: number): Promise<AutomationExecution> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/executions/${executionId}/cancel`,
		);
		return response;
	}

	async retryExecution(executionId: number): Promise<AutomationExecution> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/executions/${executionId}/retry`,
		);
		return response;
	}

	async getStepExecutions(executionId: number): Promise<StepExecution[]> {
		const response = await ApiService.apiGet<Array<StepExecution>>(
			`${API_PREFIX}/executions/${executionId}/steps`,
		);
		return response;
	}

	// ===================
	// DIRECT EXECUTION
	// ===================

	async executeFlow(
		flowId: number,
		data: {
			entityData?: Record<string, any>;
			versionId?: number;
		},
	): Promise<{ executionId: number; message: string; status: string }> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/execute`,
			data,
		);
		return response;
	}

	// ===================
	// TEMPLATE OPERATIONS
	// ===================

	async getTemplates(params?: {
		category?: string;
		isSystem?: boolean;
	}): Promise<FlowTemplate[]> {
		const response = await ApiService.apiGet<Array<FlowTemplate>>(
			`${API_PREFIX}/templates`,
			{ params },
		);
		return response;
	}

	async getTemplate(templateId: number): Promise<FlowTemplate> {
		const response = await ApiService.apiGet<FlowTemplate>(
			`${API_PREFIX}/templates/${templateId}`,
		);
		return response;
	}

	async createTemplate(data: {
		name: string;
		description?: string;
		category: string;
		templateData: any;
	}): Promise<FlowTemplate> {
		const response = await ApiService.apiPost(`${API_PREFIX}/templates`, data);
		return response;
	}

	async deleteTemplate(templateId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/templates/${templateId}`);
	}

	// ===================
	// ANALYTICS OPERATIONS
	// ===================

	async getFlowAnalytics(
		flowId: number,
		params?: {
			startDate?: string;
			endDate?: string;
		},
	): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/flows/${flowId}/analytics`,
			{ params },
		);
		return response;
	}

	// ===================
	// LIST OPERATIONS
	// ===================

	async getListConfigurations(objectType: string = 'client'): Promise<any[]> {
		const response = await ApiService.apiGet<Array<unknown>>(
			'/list-config/configurations',
			{ object_type: objectType },
		);
		return response;
	}

	async getListData(configId: string, listId: string): Promise<any> {
		const response = await ApiService.apiGet(
			`/list-config/configurations/${configId}/lists/${listId}`,
		);
		return response;
	}

	// ===================
	// STEP TYPE OPERATIONS
	// ===================

	static async getStepTypes(): Promise<
		Array<{
			type: string;
			name: string;
			description: string;
			icon?: string;
			configSchema?: Record<string, any>;
			outputSchema?: Record<string, any>;
		}>
	> {
		const response = await ApiService.apiGet<
			Array<{
				type: string;
				name: string;
				description: string;
				icon?: string;
				configSchema?: Record<string, unknown>;
				outputSchema?: Record<string, unknown>;
			}>
		>(`${API_PREFIX}/step-types`);
		return response;
	}

	static async getDataLoaderTypes(): Promise<
		Array<{
			dataType: string;
			name: string;
			description: string;
			icon: string;
			inputSchema: Record<string, any>;
			outputSchema: Record<string, any>;
		}>
	> {
		const response = await ApiService.apiGet<
			Array<{
				dataType: string;
				name: string;
				description: string;
				icon: string;
				inputSchema: Record<string, unknown>;
				outputSchema: Record<string, unknown>;
			}>
		>(`${API_PREFIX}/data-loader-types`);
		return response;
	}

	// ===================
	// WORKFLOW OPERATIONS
	// ===================

	async saveWorkflow(
		flowId: number,
		steps: Array<{
			internalId: string;
			id?: number;
			stepType: string;
			stepName: string;
			config?: any;
			positionX?: number;
			positionY?: number;
		}>,
		connections: Array<{
			internalId: string;
			id?: number;
			fromStepInternalId: string;
			toStepInternalId: string;
			conditionKey: string;
		}>,
		versionOptions?: {
			versionId?: number;
			versionName?: string;
			versionDescription?: string;
			makeActive?: boolean;
			allowDraftOverwrite?: boolean;
		},
	): Promise<any> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/flows/${flowId}/workflow`,
			{
				steps,
				connections,
				...versionOptions,
			},
		);
		return response;
	}

	// ===================
	// VERSION OPERATIONS
	// ===================

	async getFlowVersions(
		flowId: number,
		options?: ApiRequestOptions,
	): Promise<{
		versions: Array<{
			id: number;
			flowId: number;
			versionNumber: number;
			name?: string;
			description?: string;
			createdAt: string;
			updatedAt: string;
		}>;
		draftVersion: {
			id: number;
			flowId: number;
			versionNumber: number;
			name?: string;
			description?: string;
			createdAt: string;
			updatedAt: string;
		} | null;
		activeVersionId: number | null;
	}> {
		const response = await ApiService.apiGet<{
			versions: Array<FlowVersionSummary>;
			draftVersion: FlowVersionSummary | null;
			activeVersionId: number | null;
		}>(`${API_PREFIX}/flows/${flowId}/versions`, undefined, options);
		return response;
	}

	async getFlowVersion(
		flowId: number,
		versionId: number,
		options?: ApiRequestOptions,
	): Promise<{
		flow: AutomationFlow;
		steps: AutomationStep[];
		connections: StepConnection[];
		currentVersion: any;
	}> {
		const response = await ApiService.apiGet<{
			flow: AutomationFlow;
			steps: Array<AutomationStep>;
			connections: Array<StepConnection>;
			currentVersion: unknown;
		}>(
			`${API_PREFIX}/flows/${flowId}/versions/${versionId}`,
			undefined,
			options,
		);
		return response;
	}

	async setActiveVersion(flowId: number, versionId: number): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/versions/${versionId}/activate`,
		);
		return response;
	}

	async commitVersion(
		flowId: number,
		versionId: number,
		data?: { steps?: any[]; connections?: any[] },
	): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/versions/${versionId}/commit`,
			data,
		);
		return response;
	}

	async promoteDraftToActive(flowId: number, makeActive = true): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/flows/${flowId}/promote-draft`,
			{ makeActive },
		);
		return response;
	}

	async deleteVersion(flowId: number, versionId: number): Promise<void> {
		await ApiService.apiDelete(
			`${API_PREFIX}/flows/${flowId}/versions/${versionId}`,
		);
	}

	// ===================
	// BATCH OPERATIONS
	// ===================

	async getBatches(params?: StandardListParams): Promise<{
		success: boolean;
		data: Array<unknown>;
		pagination: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	}> {
		const response = await ApiService.apiGet<ListResponse<unknown>>(
			`${API_PREFIX}/batches`,
			{
				page: params?.page,
				limit: params?.pageSize,
				search: params?.search,
				sortFields:
					params?.sort != null ? JSON.stringify(params.sort) : undefined,
				filters:
					typeof params?.filters === 'object'
						? JSON.stringify(params.filters)
						: params?.filters,
				cacheKey: params?.cacheKey,
				cacheTTL: params?.cacheTTL,
			},
		);

		return response;
	}

	async getBatch(batchId: number): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/batches/${batchId}`,
		);
		return response;
	}

	async createBatch(data: any): Promise<any> {
		const response = await ApiService.apiPost(`${API_PREFIX}/batches`, data);
		return response;
	}

	async cloneBatch(
		batchId: number,
		options?: { name?: string; description?: string },
	): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/clone`,
			options ?? {},
		);
		return response;
	}

	async updateBatch(batchId: number, data: any): Promise<any> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/batches/${batchId}`,
			data,
		);
		return response;
	}

	async scheduleBatch(
		batchId: number,
		data: { scheduledFor: string },
	): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/schedule`,
			data,
		);
		return response;
	}

	async addBatchEntities(
		batchId: number,
		data: { entities: any[] },
	): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/entities`,
			data,
		);
		return response;
	}

	async clearBatchEntities(batchId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/batches/${batchId}/entities`);
	}

	async startBatch(batchId: number): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/start`,
		);
		return response;
	}

	async dispatchBatch(batchId: number): Promise<{ success: boolean }> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/dispatch`,
		);
		return response;
	}

	async unscheduleBatch(batchId: number): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/unschedule`,
		);
		return response;
	}

	async stopBatch(batchId: number): Promise<{
		batchCancelled: boolean;
		pendingCancelled: number;
		activeCancelled: number;
	}> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/stop`,
		);
		return response;
	}

	async deleteBatch(batchId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/batches/${batchId}`);
	}

	// ===================
	// RECURRING BATCH OPERATIONS
	// ===================

	async createRecurringBatch(data: {
		name: string;
		description?: string;
		flowId: number;
		versionId?: number;
		isActive?: boolean;
		recurringConfig: {
			type: 'cron' | 'interval';
			cronExpression?: string;
			intervalMinutes?: number;
			timezone?: string;
			allowConcurrentRuns?: boolean;
		};
		batchConfig?: Record<string, unknown>;
		targetLists?: Array<{
			targetListId: number;
			executionMode: 'include' | 'exclude';
			variableMapping?: Record<string, string>;
		}>;
	}): Promise<unknown> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/recurring`,
			data,
		);

		return response;
	}

	async disableRecurringBatch(batchId: number): Promise<{
		success: boolean;
		message: string;
	}> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/disable`,
		);

		return response;
	}

	async enableRecurringBatch(batchId: number): Promise<{
		success: boolean;
		message: string;
	}> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/enable`,
		);

		return response;
	}

	async triggerRecurringBatch(batchId: number): Promise<{
		success: boolean;
		message: string;
		childBatchId?: number;
	}> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/trigger`,
		);

		return response;
	}

	async getRecurringBatchHistory(
		batchId: number,
		params?: { page?: number; limit?: number },
	): Promise<{
		success: boolean;
		data: Array<unknown>;
		pagination: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	}> {
		const response = await ApiService.apiGet<ListResponse<unknown>>(
			`${API_PREFIX}/batches/${batchId}/runs`,
			params,
		);

		return response;
	}

	async getBatchEntities(
		batchId: number,
		params?: {
			status?: string;
			limit?: number;
			offset?: number;
		},
	): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/batches/${batchId}/entities`,
			params,
		);
		return response;
	}

	// ===================
	// REPORTING OPERATIONS
	// ===================

	async getExecutionReports(params?: StandardListParams): Promise<unknown> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/reports/executions`,
			{
				page: params?.page,
				limit: params?.pageSize,
				search: params?.search,
				sortFields:
					params?.sort != null ? JSON.stringify(params.sort) : undefined,
				filters:
					typeof params?.filters === 'object'
						? JSON.stringify(params.filters)
						: params?.filters,
				cacheKey: params?.cacheKey,
				cacheTTL: params?.cacheTTL,
			},
		);

		return response;
	}

	async getExecutionDetails(executionId: number): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/reports/executions/${executionId}/details`,
		);
		return response;
	}

	async getFlowSummary(
		flowId: number,
		params?: {
			startDate?: string;
			endDate?: string;
		},
	): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/reports/flow/${flowId}/summary`,
			params,
		);
		return response;
	}

	// ===================
	// ADDITIONAL TRIGGER OPERATIONS
	// ===================

	async executeTrigger(triggerId: number): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/triggers/${triggerId}/execute`,
		);
		return response;
	}

	// ===================
	// CONTEXT OPERATIONS
	// ===================

	async getStepContext(
		flowId: number,
		versionId: number,
		stepId: number,
	): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/flows/${flowId}/versions/${versionId}/steps/${stepId}/context`,
		);
		return response;
	}

	// ===================
	// TARGET LIST OPERATIONS
	// ===================

	async getTargetLists(params?: StandardListParams): Promise<unknown> {
		const response = await ApiService.apiGet(`${API_PREFIX}/target-lists`, {
			page: params?.page,
			limit: params?.pageSize,
			search: params?.search,
			sortFields:
				params?.sort != null ? JSON.stringify(params.sort) : undefined,
			filters:
				typeof params?.filters === 'object'
					? JSON.stringify(params.filters)
					: params?.filters,
			cacheKey: params?.cacheKey,
			cacheTTL: params?.cacheTTL,
		});

		return response;
	}

	async getTargetList(targetListId: number): Promise<any> {
		const response = await ApiService.apiGet(
			`${API_PREFIX}/target-lists/${targetListId}`,
		);
		return response;
	}

	async createTargetList(data: {
		name: string;
		description?: string;
		listType?: 'static' | 'dynamic';
		entityType?: string;
		query?: any;
		metadata?: any;
	}): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/target-lists`,
			data,
		);
		return response;
	}

	async updateTargetList(targetListId: number, data: any): Promise<any> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/target-lists/${targetListId}`,
			data,
		);
		return response;
	}

	async deleteTargetList(targetListId: number): Promise<void> {
		await ApiService.apiDelete(`${API_PREFIX}/target-lists/${targetListId}`);
	}

	async copyTargetList(targetListId: number, name: string): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/target-lists/${targetListId}/copy`,
			{ name },
		);
		return response;
	}

	async getTargetListItems(
		targetListId: number,
		params?: {
			excluded?: boolean;
			limit?: number;
			offset?: number;
		},
	): Promise<any[]> {
		const response = await ApiService.apiGet<Array<unknown>>(
			`${API_PREFIX}/target-lists/${targetListId}/items`,
			params,
		);
		return response;
	}

	async addTargetListItems(
		targetListId: number,
		data: {
			items: Array<{
				entityId: string;
				entityType: string;
				entityData: any;
			}>;
		},
	): Promise<any> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/target-lists/${targetListId}/items`,
			data,
		);
		return response;
	}

	async updateTargetListItem(
		targetListId: number,
		itemId: number | string,
		data: {
			entityData?: any;
			excluded?: boolean;
		},
	): Promise<any> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/target-lists/${targetListId}/items/${itemId}`,
			data,
		);
		return response;
	}

	async deleteTargetListItem(
		targetListId: number,
		itemId: number,
	): Promise<void> {
		await ApiService.apiDelete(
			`${API_PREFIX}/target-lists/${targetListId}/items/${itemId}`,
		);
	}

	async testTargetListQuery(
		targetListId: number,
		query: any,
	): Promise<{
		count: number;
		sample?: any[];
	}> {
		const response = await ApiService.apiPost(
			`${API_PREFIX}/target-lists/${targetListId}/test-query`,
			query,
		);
		return response;
	}

	async getTargetListUsage(targetListId: number): Promise<any[]> {
		const response = await ApiService.apiGet<Array<unknown>>(
			`${API_PREFIX}/target-lists/${targetListId}/usage`,
		);
		return response;
	}

	async exportTargetListCsv(targetListId: number): Promise<string> {
		const response = await ApiService.apiGet<string>(
			`${API_PREFIX}/target-lists/${targetListId}/export-csv`,
		);
		return response;
	}

	// ===================
	// BATCH TARGET LIST OPERATIONS
	// ===================

	async getBatchTargetLists(batchId: number): Promise<any[]> {
		const response = await ApiService.apiGet<Array<unknown>>(
			`${API_PREFIX}/batches/${batchId}/target-lists`,
		);
		return response;
	}

	async addBatchTargetList(
		batchId: number,
		data: {
			sourceType?: 'static-list' | 'group' | 'saved-list';
			targetListId?: number;
			groupId?: number;
			entityType?: string;
			savedListId?: number;
			executionMode: 'include' | 'exclude';
		},
	): Promise<any> {
		// Server expects an array of target lists wrapped in targetLists property
		const response = await ApiService.apiPost(
			`${API_PREFIX}/batches/${batchId}/target-lists`,
			{
				targetLists: [data],
			},
		);
		return response;
	}

	async updateBatchTargetList(
		batchId: number,
		batchTargetListId: number,
		data: {
			executionMode?: 'include' | 'exclude';
			variableMapping?: Record<string, string>;
		},
	): Promise<any> {
		const response = await ApiService.apiPut(
			`${API_PREFIX}/batches/${batchId}/target-lists/${batchTargetListId}`,
			data,
		);
		return response;
	}

	async removeBatchTargetList(
		batchId: number,
		batchTargetListId: number,
	): Promise<void> {
		await ApiService.apiDelete(
			`${API_PREFIX}/batches/${batchId}/target-lists/${batchTargetListId}`,
		);
	}

	// Target Type endpoints
	async getTargetTypes(): Promise<any[]> {
		const response = await ApiService.apiGet<
			ApiResponseDataModel<Array<unknown>>
		>(`${API_PREFIX}/target-types`);
		return response.data || [];
	}

	async getTargetType(type: string): Promise<any> {
		const response = await ApiService.apiGet<ApiResponseDataModel<unknown>>(
			`${API_PREFIX}/target-types/${type}`,
		);
		return response.data;
	}

	async previewTargetType(
		type: string,
		filters: any,
		limit?: number,
	): Promise<any[]> {
		const payload: any = { filters };
		// Only include limit if explicitly provided
		if (limit !== undefined) {
			payload.limit = limit;
		}
		const response = await ApiService.apiPost(
			`${API_PREFIX}/target-types/${type}/preview`,
			payload,
		);
		return response;
	}

	async previewTargetList(
		targetListId: number,
		limit?: number,
	): Promise<{ data: any[]; total: number }> {
		const params = new URLSearchParams();
		if (limit != null) {
			params.append('limit', limit.toString());
		}
		const url = `${API_PREFIX}/target-lists/${targetListId}/preview${params.toString() ? `?${params.toString()}` : ''}`;
		const response = await ApiService.apiGet<{
			data?: Array<unknown>;
			total?: number;
		}>(url);
		return { data: response.data || [], total: response.total || 0 };
	}

	async getSuggestedMappings(
		type: string,
		flowId?: number,
		versionId?: number,
	): Promise<any> {
		const params = new URLSearchParams();
		if (flowId) {
			params.append('flowId', flowId.toString());
		}
		if (versionId) {
			params.append('versionId', versionId.toString());
		}

		const response = await ApiService.apiGet(
			`${API_PREFIX}/target-types/${type}/suggested-mappings?${params.toString()}`,
		);
		return response;
	}

	// ===================
	// STEP REPORTING
	// ===================

	async getStepReports(params?: {
		batchId?: number;
		stepId?: number;
		stepType?: string;
		flowId?: number;
		startDate?: string;
		endDate?: string;
		executionStatus?: string[];
		entityType?: string;
		entityIds?: string[];
	}): Promise<any[]> {
		const queryParams = new URLSearchParams();

		if (params?.batchId) {
			queryParams.append('batchId', params.batchId.toString());
		}
		if (params?.stepId) {
			queryParams.append('stepId', params.stepId.toString());
		}
		if (params?.stepType) {
			queryParams.append('stepType', params.stepType);
		}
		if (params?.flowId) {
			queryParams.append('flowId', params.flowId.toString());
		}
		if (params?.startDate) {
			queryParams.append('startDate', params.startDate);
		}
		if (params?.endDate) {
			queryParams.append('endDate', params.endDate);
		}
		if (params?.executionStatus) {
			params.executionStatus.forEach((status) => {
				queryParams.append('executionStatus', status);
			});
		}
		if (params?.entityType) {
			queryParams.append('entityType', params.entityType);
		}
		if (params?.entityIds) {
			params.entityIds.forEach((id) => {
				queryParams.append('entityIds', id);
			});
		}

		const response = await ApiService.apiGet<Array<unknown>>(
			`${API_PREFIX}/step-reports?${queryParams.toString()}`,
		);
		return response;
	}

	async getStepExecutionReport(stepExecutionId: number): Promise<{
		success: boolean;
		data: any;
	}> {
		const response = await ApiService.apiGet<{
			success: boolean;
			data: unknown;
		}>(`${API_PREFIX}/step-executions/${stepExecutionId}/report`);
		return response;
	}

	/**
	 * Get aggregated flow metrics for a batch - step-by-step execution counts and outcomes.
	 */
	async getBatchFlowMetrics(batchId: number): Promise<{
		batchId: number;
		versionId: number;
		flowName: string;
		totalExecutions: number;
		completedExecutions: number;
		failedExecutions: number;
		pendingExecutions: number;
		steps: Array<{
			stepId: number;
			stepInternalId: string;
			stepName: string;
			stepType: string;
			stepOrder: number;
			parentStepId: number | null;
			totalReached: number;
			completed: number;
			failed: number;
			skipped: number;
			pending: number;
			outcomeBreakdown: Array<{
				key: string;
				label: string;
				count: number;
				percentage: number;
				metadata?: Record<string, unknown>;
			}>;
			outgoingConnections: Array<{
				conditionKey: string;
				toStepId: number;
				toStepInternalId: string;
				executionCount: number;
			}>;
		}>;
	} | null> {
		const response = await ApiService.apiGet<FlowMetricsResponse | null>(
			`${API_PREFIX}/batches/${batchId}/flow-metrics`,
		);
		return response;
	}

	/**
	 * Get step executions filtered by outcome for drill-down.
	 */
	async getStepExecutionsByOutcome(
		batchId: number,
		stepId: number,
		params?: {
			outcomeKey?: string;
			filterPath?: string;
			filterValue?: string;
			emailEventType?: string;
			emailEventNegate?: boolean;
			page?: number;
			pageSize?: number;
		},
	): Promise<{
		success: boolean;
		data: Array<{
			id: number;
			executionId: number;
			status: string;
			startedAt: string | null;
			completedAt: string | null;
			durationMs: number | null;
			errorMessage: string | null;
			outcomeKey: string | null;
			entity: {
				type: string | null;
				id: string | null;
				label: string | null;
			};
			execution: {
				id: number;
				status: string;
			};
		}>;
		pagination: {
			total: number;
			page: number;
			pageSize: number;
			totalPages: number;
		};
	}> {
		const queryParams = new URLSearchParams();

		if (params?.outcomeKey != null) {
			queryParams.append('outcomeKey', params.outcomeKey);
		}
		if (params?.filterPath != null) {
			queryParams.append('filterPath', params.filterPath);
		}
		if (params?.filterValue != null) {
			queryParams.append('filterValue', params.filterValue);
		}
		if (params?.emailEventType != null) {
			queryParams.append('emailEventType', params.emailEventType);
		}
		if (params?.emailEventNegate != null) {
			queryParams.append(
				'emailEventNegate',
				params.emailEventNegate.toString(),
			);
		}
		if (params?.page != null) {
			queryParams.append('page', params.page.toString());
		}
		if (params?.pageSize != null) {
			queryParams.append('pageSize', params.pageSize.toString());
		}

		const queryString = queryParams.toString();
		const url = `${API_PREFIX}/batches/${batchId}/steps/${stepId}/executions${queryString.length > 0 ? `?${queryString}` : ''}`;

		const response =
			await ApiService.apiGet<StepExecutionsByOutcomeResponse>(url);
		return response;
	}

	/**
	 * Get deep analytics for a specific step within a batch.
	 * Returns type-specific analytics (agent_call, send_email, or generic fallback).
	 * @param granularity - Time bucketing: 'auto' | 'hourly' | 'daily' | 'weekly'
	 */
	async getStepAnalytics(
		batchId: number,
		stepId: number,
		granularity: string = 'auto',
	): Promise<{
		success: boolean;
		stepId: number;
		stepName: string;
		stepType: string;
		batchId: number;
		granularity: string;
		availableGranularities: Array<string>;
		analytics: unknown;
	}> {
		const response = await ApiService.apiGet<{
			success: boolean;
			stepId: number;
			stepName: string;
			stepType: string;
			batchId: number;
			granularity: string;
			availableGranularities: Array<string>;
			analytics: unknown;
		}>(`${API_PREFIX}/batches/${batchId}/steps/${stepId}/analytics`, {
			params: { granularity },
		});
		return response;
	}

	// ===================
	// FLOW VERSION METRICS
	// ===================

	/**
	 * Get aggregated flow metrics for a specific flow version - step-by-step execution counts and outcomes.
	 * Optionally filter by date range.
	 */
	async getFlowVersionMetrics(
		flowId: number,
		versionId: number,
		params?: {
			startDate?: string;
			endDate?: string;
		},
	): Promise<{
		batchId: number;
		versionId: number;
		flowName: string;
		totalExecutions: number;
		completedExecutions: number;
		failedExecutions: number;
		pendingExecutions: number;
		steps: Array<{
			stepId: number;
			stepInternalId: string;
			stepName: string;
			stepType: string;
			stepOrder: number;
			parentStepId: number | null;
			totalReached: number;
			completed: number;
			failed: number;
			skipped: number;
			pending: number;
			outcomeBreakdown: Array<{
				key: string;
				label: string;
				count: number;
				percentage: number;
				metadata?: Record<string, unknown>;
			}>;
			outgoingConnections: Array<{
				conditionKey: string;
				toStepId: number;
				toStepInternalId: string;
				executionCount: number;
			}>;
		}>;
	} | null> {
		const queryParams = new URLSearchParams();
		if (params?.startDate != null) {
			queryParams.append('startDate', params.startDate);
		}
		if (params?.endDate != null) {
			queryParams.append('endDate', params.endDate);
		}
		const queryString = queryParams.toString();
		const url = `${API_PREFIX}/flows/${flowId}/versions/${versionId}/metrics${queryString.length > 0 ? `?${queryString}` : ''}`;
		const response = await ApiService.apiGet<FlowMetricsResponse | null>(url);
		return response;
	}

	/**
	 * Get step executions filtered by outcome for a flow version (drill-down).
	 */
	async getFlowVersionStepExecutions(
		flowId: number,
		versionId: number,
		stepId: number,
		params?: {
			outcomeKey?: string;
			filterPath?: string;
			filterValue?: string;
			emailEventType?: string;
			emailEventNegate?: boolean;
			page?: number;
			pageSize?: number;
		},
	): Promise<{
		success: boolean;
		data: Array<{
			id: number;
			executionId: number;
			status: string;
			startedAt: string | null;
			completedAt: string | null;
			durationMs: number | null;
			errorMessage: string | null;
			outcomeKey: string | null;
			entity: {
				type: string | null;
				id: string | null;
				label: string | null;
			};
			execution: {
				id: number;
				status: string;
			};
		}>;
		pagination: {
			total: number;
			page: number;
			pageSize: number;
			totalPages: number;
		};
	}> {
		const queryParams = new URLSearchParams();

		if (params?.outcomeKey != null) {
			queryParams.append('outcomeKey', params.outcomeKey);
		}
		if (params?.filterPath != null) {
			queryParams.append('filterPath', params.filterPath);
		}
		if (params?.filterValue != null) {
			queryParams.append('filterValue', params.filterValue);
		}
		if (params?.emailEventType != null) {
			queryParams.append('emailEventType', params.emailEventType);
		}
		if (params?.emailEventNegate != null) {
			queryParams.append(
				'emailEventNegate',
				params.emailEventNegate.toString(),
			);
		}
		if (params?.page != null) {
			queryParams.append('page', params.page.toString());
		}
		if (params?.pageSize != null) {
			queryParams.append('pageSize', params.pageSize.toString());
		}

		const queryString = queryParams.toString();
		const url = `${API_PREFIX}/flows/${flowId}/versions/${versionId}/steps/${stepId}/executions${queryString.length > 0 ? `?${queryString}` : ''}`;

		const response =
			await ApiService.apiGet<StepExecutionsByOutcomeResponse>(url);
		return response;
	}

	// ===================
	// EVENT TYPE OPERATIONS
	// ===================

	async getEventTypes(): Promise<
		Array<{
			key: string;
			label: string;
			description: string;
			outputSchema: Record<
				string,
				{
					type: string;
					label: string;
					description?: string;
				}
			>;
		}>
	> {
		const response = await ApiService.apiGet<
			Array<{
				key: string;
				label: string;
				description: string;
				outputSchema: Record<
					string,
					{
						type: string;
						label: string;
						description?: string;
					}
				>;
			}>
		>(`${API_PREFIX}/event-types`);
		return response;
	}

	// ===================
	// GROUP OPERATIONS (for batch sources)
	// ===================

	async getGroups(params?: { search?: string; limit?: number }): Promise<{
		data: Array<{ id: number; name: string; description?: string }>;
	}> {
		const queryParams = new URLSearchParams();
		if (params?.search != null) {
			queryParams.append('search', params.search);
		}
		if (params?.limit != null) {
			queryParams.append('limit', params.limit.toString());
		}
		const queryString = queryParams.toString();
		const response = await ApiService.apiGet<{
			data: Array<{ id: number; name: string; description?: string }>;
		}>(`/groups${queryString.length > 0 ? `?${queryString}` : ''}`);
		return response;
	}

	// ===================
	// SAVED SEARCH OPERATIONS (for batch sources)
	// ===================

	async getSavedSearches(params?: {
		search?: string;
		listKey?: string;
		limit?: number;
	}): Promise<{
		data: Array<{
			id: number;
			name: string;
			listKey: string;
			description?: string;
		}>;
	}> {
		const queryParams = new URLSearchParams();
		if (params?.search != null) {
			queryParams.append('search', params.search);
		}
		if (params?.listKey != null) {
			queryParams.append('listKey', params.listKey);
		}
		if (params?.limit != null) {
			queryParams.append('limit', params.limit.toString());
		}
		const queryString = queryParams.toString();
		const response = await ApiService.apiGet<{
			data: Array<{
				id: number;
				name: string;
				listKey: string;
				description?: string;
			}>;
		}>(`/saved-searches${queryString.length > 0 ? `?${queryString}` : ''}`);
		return response;
	}
}
